/**
 * Utility to detect the current page name based on the route path
 */

import { ROUTES } from "../../configs/routes";

export interface PageDetectionResult {
  pageName: string;
  routePath: string;
}

/**
 * Mapping of routes to descriptive page names
 */
const ROUTE_TO_PAGE_NAME: Record<string, string> = {
  [ROUTES.ROOT]: "landing",
  [ROUTES.LOGIN]: "login",
  [ROUTES.MYDATA]: "mydata",
  [ROUTES.MYDATA_HISTORY]: "mydata-history",
  [ROUTES.SUMMARY_REQUEST_DETAILS]: "mydata-request-details",
  [ROUTES.MYDATA_HISTORY_REQUEST_DETAILS]: "history-request-details",
  [ROUTES.PRIVACY_POLICY]: "privacy-policy",
  [ROUTES.POLICY_OVERVIEW]: "policy-overview",
  [ROUTES.COOKIE_POLICY]: "cookie-policy",
  [ROUTES.ABOUT_OVERVIEW]: "about-overview",
  [ROUTES.ABOUT_GRPS]: "about-grps",
  [ROUTES.ERROR_400]: "error-400",
  [ROUTES.ERROR_403]: "error-403",
  [ROUTES.ERROR_404]: "error-404",
  [ROUTES.ERROR_500]: "error-500",
  [ROUTES.ERROR_502]: "error-502",
  [ROUTES.ERROR_503]: "error-503",
  [ROUTES.ERROR_IE]: "error-ie",
  [ROUTES.ERROR_MAINTENANCE]: "error-maintenance",
  [ROUTES.BDP_CONSIGNMENT]: "consignment-bdp",
  [ROUTES.ANALYTICS_CONSIGNMENT]: "consignment-analytics",
  [ROUTES.DIAGMON_CONSIGNMENT]: "consignment-diagmon",
  [ROUTES.DIGITAL_LEGACY_AUTH]: "digital-legacy-auth",
  [ROUTES.ADSSO_LOGOUT]: "logout",
};

/**
 * Detects the current page based on the given pathname
 * @param pathname - The current route pathname
 * @returns Page detection result with page name and route path
 */
export const detectPage = (pathname: string): PageDetectionResult => {
  // Check for exact route matches first
  if (ROUTE_TO_PAGE_NAME[pathname]) {
    return {
      pageName: ROUTE_TO_PAGE_NAME[pathname],
      routePath: pathname,
    };
  }

  // Check for dynamic routes (e.g., /my-data/:requestNumber)
  if (pathname.startsWith(ROUTES.MYDATA) && pathname !== ROUTES.MYDATA) {
    return {
      pageName: "mydata-request-details",
      routePath: ROUTES.MYDATA,
    };
  }

  // Handle old route format for backwards compatibility
  if (pathname.startsWith("/mydata/") && pathname !== "/mydata") {
    return {
      pageName: "mydata-request-details",
      routePath: ROUTES.MYDATA,
    };
  }

  if (pathname.startsWith(ROUTES.MYDATA_HISTORY) && pathname !== ROUTES.MYDATA_HISTORY) {
    return {
      pageName: "history-request-details",
      routePath: ROUTES.MYDATA_HISTORY,
    };
  }

  // Default to unknown if route doesn't match
  return {
    pageName: "unknown",
    routePath: pathname,
  };
};

/**
 * Converts a string to kebab-case
 */
const kebabCase = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, " ") // Replace special characters with spaces
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-") // Replace multiple hyphens with single
    .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
};

/**
 * Formats a test-id with the page prefix
 * @param pathname - The current route pathname
 * @param descriptiveId - The descriptive part of the test-id
 * @returns Formatted test-id string
 */
export const formatTestId = (pathname: string, descriptiveId: string): string => {
  const { pageName } = detectPage(pathname);
  return `${pageName}-${kebabCase(descriptiveId)}`;
};
